import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../hooks/useAuthStore'
import { PageHeader } from '../components/PageHeader'
import { DashboardCard } from '../components/DashboardCard'
import { TabNavigation } from '../components/TabNavigation'
import { CalendarDaysIcon, AcademicCapIcon, BanknotesIcon, CheckCircleIcon, ClockIcon, UserGroupIcon, ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline'

interface DashboardStats {
  todayStudents: {
    present: number
    total: number
  }
  pendingPayments: number
  pendingExpenses: number
  todayLessonPosted: boolean
  todayExpensesTotal: number
}

export function AdminDashboard() {
  const navigate = useNavigate()
  const { logout } = useAuthStore()
  const [stats] = useState<DashboardStats>({
    todayStudents: { present: 16, total: 18 },
    pendingPayments: 5,
    pendingExpenses: 3,
    todayLessonPosted: false,
    todayExpensesTotal: 245
  })

  // Format date in Thai Buddhist Era
  const formatThaiDate = (date: Date) => {
    const thaiYear = date.getFullYear() + 543
    const months = [
      'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
      'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
    ]
    const days = ['อาทิตย์', 'จันทร์', 'อังคาร', 'พุธ', 'พฤหัสบดี', 'ศุกร์', 'เสาร์']
    
    return `วัน${days[date.getDay()]}ที่ ${date.getDate()} ${months[date.getMonth()]} ${thaiYear}`
  }

  const today = formatThaiDate(new Date())

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader 
        title="แดชบอร์ดผู้ดูแล"
        showBackButton={false}
        actions={
          <button
            onClick={handleLogout}
            className="text-gray-600 hover:text-red-600 p-2 rounded-lg hover:bg-red-50 transition-colors"
            title="ออกจากระบบ"
          >
            <ArrowRightOnRectangleIcon className="h-5 w-5" />
          </button>
        }
      />
      
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Today's Date */}
        <div className="mb-6 text-center">
          <p className="text-lg text-gray-600">{today}</p>
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">⚡ การดำเนินการด่วน</h2>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <button
              onClick={() => navigate('/admin/attendance')}
              className="p-4 rounded-lg border-2 bg-purple-50 border-purple-300 hover:bg-purple-100 transition-colors"
            >
              <div className="flex items-center">
                <CheckCircleIcon className="h-6 w-6 text-purple-700 mr-3" />
                <span className="font-medium">เช็คชื่อ</span>
              </div>
              <p className="text-sm text-purple-700 mt-2">เช็คชื่อนักเรียนวันนี้</p>
            </button>
            
            <button
              onClick={() => navigate('/admin/lessons/post')}
              className={`p-4 rounded-lg border-2 ${
                !stats.todayLessonPosted 
                  ? 'bg-yellow-50 border-yellow-300 hover:bg-yellow-100'
                  : 'bg-green-50 border-green-300'
              } transition-colors`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <AcademicCapIcon className="h-6 w-6 text-gray-700 mr-3" />
                  <span className="font-medium">โพสต์บทเรียน</span>
                </div>
                {stats.todayLessonPosted && (
                  <CheckCircleIcon className="h-5 w-5 text-green-600" />
                )}
              </div>
              {!stats.todayLessonPosted && (
                <p className="text-sm text-yellow-700 mt-2">ยังไม่ได้โพสต์วันนี้</p>
              )}
            </button>

            <button
              onClick={() => navigate('/admin/expenses/add')}
              className="p-4 bg-white rounded-lg border-2 border-gray-200 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center">
                <BanknotesIcon className="h-6 w-6 text-gray-700 mr-3" />
                <span className="font-medium">บันทึกค่าใช้จ่าย</span>
              </div>
              {stats.todayExpensesTotal > 0 && (
                <p className="text-sm text-gray-600 mt-2">วันนี้: ฿{stats.todayExpensesTotal}</p>
              )}
            </button>

            <button
              onClick={() => navigate('/admin/payments/verify')}
              className={`p-4 rounded-lg border-2 ${
                stats.pendingPayments > 0
                  ? 'bg-red-50 border-red-300 hover:bg-red-100'
                  : 'bg-white border-gray-200 hover:bg-gray-50'
              } transition-colors`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <CheckCircleIcon className="h-6 w-6 text-gray-700 mr-3" />
                  <span className="font-medium">ตรวจสอบสลิป</span>
                </div>
                {stats.pendingPayments > 0 && (
                  <span className="bg-red-600 text-white text-xs font-bold px-2 py-1 rounded-full">
                    {stats.pendingPayments}
                  </span>
                )}
              </div>
            </button>
          </div>
        </div>

        {/* Statistics Overview */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">📊 สถานะวันนี้</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <DashboardCard
              title="นักเรียนมาเรียน"
              value={`${stats.todayStudents.present}/${stats.todayStudents.total}`}
              icon={<UserGroupIcon className="h-5 w-5" />}
              subtitle="คน"
              variant={stats.todayStudents.present === stats.todayStudents.total ? 'success' : 'warning'}
            />
            
            <DashboardCard
              title="รอตรวจสอบการชำระ"
              value={stats.pendingPayments.toString()}
              icon={<ClockIcon className="h-5 w-5" />}
              subtitle="รายการ"
              variant={stats.pendingPayments > 0 ? 'error' : 'default'}
            />
            
            <DashboardCard
              title="ค่าใช้จ่ายรอเรียกเก็บ"
              value={stats.pendingExpenses.toString()}
              icon={<BanknotesIcon className="h-5 w-5" />}
              subtitle="รายการ"
              variant={stats.pendingExpenses > 0 ? 'warning' : 'default'}
            />
            
            <DashboardCard
              title="บทเรียนวันนี้"
              value={stats.todayLessonPosted ? 'โพสต์แล้ว' : 'ยังไม่โพสต์'}
              icon={<AcademicCapIcon className="h-5 w-5" />}
              subtitle=""
              variant={stats.todayLessonPosted ? 'success' : 'warning'}
            />
          </div>
        </div>

        {/* Recent Activities */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">📋 กิจกรรมล่าสุด</h2>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="divide-y divide-gray-200">
              <div className="p-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">คุณสมศรี ส่งสลิปชำระค่าเรียน</p>
                    <p className="text-sm text-gray-500">นักเรียน: น้องใหม่ | จำนวน: ฿1,600</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">14:30 น.</p>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      รอตรวจสอบ
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="p-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">บันทึกค่าขนมนักเรียน</p>
                    <p className="text-sm text-gray-500">3 รายการ | รวม: ฿145</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">13:15 น.</p>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      แจ้งผู้ปกครองแล้ว
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="p-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">นักเรียนเข้าเรียน</p>
                    <p className="text-sm text-gray-500">น้องพิณ ลงชื่อเข้าเรียน</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">09:05 น.</p>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      มาตรงเวลา
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="px-4 py-3 bg-gray-50 text-center">
              <button className="text-sm text-blue-600 hover:text-blue-800 font-medium">
                ดูกิจกรรมทั้งหมด →
              </button>
            </div>
          </div>
        </div>

        {/* Upcoming Tasks */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">📅 งานที่ต้องทำ</h2>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <ul className="space-y-3">
              <li className="flex items-start">
                <CalendarDaysIcon className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                <div>
                  <p className="font-medium text-gray-900">เตรียมบทเรียนสัปดาห์หน้า</p>
                  <p className="text-sm text-gray-500">วันเสาร์ที่ 24 ส.ค.</p>
                </div>
              </li>
              <li className="flex items-start">
                <BanknotesIcon className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                <div>
                  <p className="font-medium text-gray-900">ส่งใบแจ้งหนี้ประจำเดือน</p>
                  <p className="text-sm text-gray-500">วันที่ 25 ส.ค.</p>
                </div>
              </li>
              <li className="flex items-start">
                <UserGroupIcon className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                <div>
                  <p className="font-medium text-gray-900">ประชุมผู้ปกครอง</p>
                  <p className="text-sm text-gray-500">วันเสาร์ที่ 31 ส.ค. เวลา 16:00 น.</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <TabNavigation activeTab="dashboard" />
    </div>
  )
}